import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import LazyImage from '../../../components/LazyImage';
import { friendshipService } from '../../../services/socialService';

const FriendDiscovery = ({ isOpen, onClose, userId, onFriendAdded }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingFriendId, setAddingFriendId] = useState(null);

  const handleSearch = async () => {
    if (!searchTerm?.trim()) return;

    setLoading(true);
    try {
      const results = await friendshipService?.searchUsers(searchTerm, userId);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFriend = async (friendId) => {
    setAddingFriendId(friendId);
    try {
      await friendshipService?.sendFriendRequest(userId, friendId);
      onFriendAdded();
      setSearchResults(prev => prev?.filter(u => u?.id !== friendId));
    } catch (error) {
      console.error('Error sending friend request:', error);
    } finally {
      setAddingFriendId(null);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSearchResults([]);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={handleClose}
      />
      
      <div className="relative w-full max-w-2xl bg-card rounded-xl shadow-wellness-lg animate-scale-in elevation-3 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/40">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Descubrir Amigos</h2>
            <p className="text-sm text-muted-foreground">Busca y agrega amigos para competir</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Search Bar */}
          <div className="flex space-x-3 mb-6">
            <div className="flex-1">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                placeholder="Buscar por nombre o email..."
                onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={!searchTerm?.trim() || loading}
              loading={loading}
              iconName="Search"
            >
              Buscar
            </Button>
          </div>

          {/* Search Results */}
          {searchResults?.length > 0 ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Resultados ({searchResults?.length})
              </h3>
              {searchResults?.map((user) => (
                <div
                  key={user?.id}
                  className="flex items-center justify-between p-4 bg-muted/20 rounded-xl border border-border hover:border-primary/50 transition-all"
                >
                  <div className="flex items-center space-x-3">
                    {user?.avatar_url ? (
                      <LazyImage
                        src={user?.avatar_url}
                        alt={user?.full_name || 'User avatar'}
                        className="w-12 h-12 rounded-full"
                        sizes="48px"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-lg font-semibold text-primary">
                          {user?.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-foreground">{user?.full_name}</p>
                      <p className="text-sm text-muted-foreground">{user?.email}</p>
                      <p className="text-xs text-muted-foreground">{user?.total_points} puntos</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAddFriend(user?.id)}
                    disabled={addingFriendId === user?.id}
                    loading={addingFriendId === user?.id}
                    iconName="UserPlus"
                    size="sm"
                  >
                    Enviar Solicitud
                  </Button>
                </div>
              ))}
            </div>
          ) : searchTerm && !loading ? (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No se encontraron usuarios</p>
              <p className="text-sm text-muted-foreground mt-2">Intenta con otro nombre</p>
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Busca amigos por nombre</p>
              <p className="text-sm text-muted-foreground mt-2">Escribe un nombre y presiona buscar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FriendDiscovery;